import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { entwurfSchluessel, leseEntwurf, schreibeEntwurf } from '../../lib/entwuerfe';
import { ACCOUNTS } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { isSubSession } from '../../utils/subSession';
import { conversationsUrl, SCOPE_CHANGED_EVENT } from '../../utils/conversationScope';

const API = '/api';

// --- Types ---
interface Conversation {
  sessionId: string;
  accountId: string;
  accountLabel: string;
  accountColor: string;
  projectPath: string;
  projectName: string;
  summary: string;
  customName: string;
  status: 'ongoing' | 'completed';
  streamingId: string | null;
  model: string;
  messageCount: number;
  updatedAt: string;
  createdAt: string;
}

interface QueueOverlayProps {
  accountId: string;
  projectId?: string;
  workDir?: string;
  useLocal?: boolean;
  onNavigate: (sessionId: string) => void;  // Navigate CUI iframe to conversation
  onStartNew: (subject: string, message: string, model: string) => Promise<boolean>;  // Start new conversation, returns success
  refreshSignal?: number;  // Increment to trigger conversation list refresh (from parent WS)
  /** Einfache Ansicht fuer den Sales-Bereich: keine Konto-Kennungen, keine
   *  Modellwahl, keine Zaehler fuer laufende Prozesse. Ein Vertriebspartner
   *  soll ein Gespraech beginnen, nicht einen Agenten konfigurieren — und die
   *  Konto-Auswahl bot ihm bisher fremde Konten (Sahori, Kurt) an. */
  simple?: boolean;
}

// --- Helpers ---
function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'jetzt';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function duration(created: string, updated: string): string {
  if (!created || !updated) return '';
  const diff = new Date(updated).getTime() - new Date(created).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hours}h${remainMins}m` : `${hours}h`;
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max) + '...';
}

// --- Styles (injected once) ---
const STYLE_ID = 'queue-styles';
function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes q-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
    .q-row:hover { background: var(--tn-bg-highlight) !important; }
    .q-row:hover .q-actions { opacity: 1 !important; }
    .q-btn:hover { filter: brightness(1.2); }
  `;
  document.head.appendChild(style);
}

// --- Status indicator ---
function StatusIcon({ status, streaming }: { status: string; streaming: boolean }) {
  if (streaming) {
    return <span style={{
      width: 8, height: 8, borderRadius: '50%', background: 'var(--tn-green)',
      display: 'inline-block', flexShrink: 0,
      animation: 'q-pulse 1.5s ease-in-out infinite',
    }} />;
  }
  if (status === 'ongoing') {
    return <span style={{
      width: 8, height: 8, borderRadius: '50%', background: 'var(--tn-orange)',
      display: 'inline-block', flexShrink: 0,
    }} />;
  }
  // completed
  return <span style={{
    width: 8, height: 8, borderRadius: '50%', background: 'var(--tn-text-muted)',
    display: 'inline-block', flexShrink: 0, opacity: 0.5,
  }} />;
}

function titelVon(conv: Conversation): string {
  return conv.customName || truncate(conv.summary.split('\n')[0], 70) || 'Ohne Betreff';
}

/**
 * GLEICHE TITEL UNTERSCHEIDBAR MACHEN (Rafael, 25.10.2026: „lesbare Titel").
 * Zwei Chats eines Partners mit demselben Betreff standen ununterscheidbar
 * untereinander. Fuer die Partner-Sicht bekommt jeder mehrfach vorkommende Titel
 * das Anfangsdatum des Chats dazu; der gespeicherte Name bleibt unveraendert.
 */
function datumsZusaetze(convs: Conversation[]): Map<string, string> {
  const anzahl = new Map<string, number>();
  for (const c of convs) anzahl.set(titelVon(c), (anzahl.get(titelVon(c)) ?? 0) + 1);
  const zusatz = new Map<string, string>();
  for (const c of convs) {
    if ((anzahl.get(titelVon(c)) ?? 0) < 2 || !c.createdAt) continue;
    const d = new Date(c.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    zusatz.set(c.sessionId, d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }));
  }
  // Gleicher Titel UND gleicher Tag: erst die Uhrzeit macht sie unterscheidbar.
  const gleich = new Map<string, number>();
  for (const c of convs) {
    const z = zusatz.get(c.sessionId);
    if (z) gleich.set(`${titelVon(c)}\u0000${z}`, (gleich.get(`${titelVon(c)}\u0000${z}`) ?? 0) + 1);
  }
  for (const c of convs) {
    const z = zusatz.get(c.sessionId);
    if (!z || (gleich.get(`${titelVon(c)}\u0000${z}`) ?? 0) < 2) continue;
    zusatz.set(c.sessionId, new Date(c.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }));
  }
  return zusatz;
}

// --- Conversation Row ---
function ConvRow({ conv, onNavigate, onStop, onSetName, titelZusatz }: {
  conv: Conversation;
  onNavigate: () => void;
  onStop: () => void;
  onSetName: (name: string) => void;
  titelZusatz?: string;
}) {
  const isStreaming = !!conv.streamingId;
  const displayName = titelZusatz ? `${titelVon(conv)} · ${titelZusatz}` : titelVon(conv);
  const previewText = conv.customName
    ? truncate(conv.summary.split('\n')[0], 80)
    : '';

  return (
    <div
      className="q-row"
      onClick={onNavigate}
      style={{
        padding: '8px 12px', cursor: 'pointer',
        borderBottom: '1px solid var(--tn-border)',
        borderLeft: isStreaming ? '3px solid var(--tn-green)'
          : conv.status === 'ongoing' ? '3px solid var(--tn-orange)'
          : '3px solid transparent',
        transition: 'background 0.1s',
        position: 'relative',
      }}
    >
      {/* Line 1: Status + Title + Time + Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <StatusIcon status={conv.status} streaming={isStreaming} />
        <span style={{
          fontSize: 12, color: 'var(--tn-text)', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: conv.customName ? 600 : 400,
        }}>
          {displayName}
        </span>
        <span style={{ fontSize: 10, color: 'var(--tn-text-muted)', opacity: 0.5, flexShrink: 0 }}>
          {duration(conv.createdAt, conv.updatedAt)}
        </span>
        {/* Hover actions */}
        <div className="q-actions" style={{ opacity: 0, display: 'flex', gap: 4, flexShrink: 0, transition: 'opacity 0.15s' }}>
          {conv.status === 'ongoing' && isStreaming && (
            <button className="q-btn" onClick={(e) => { e.stopPropagation(); onStop(); }} style={{
              padding: '1px 6px', fontSize: 9, borderRadius: 3, cursor: 'pointer',
              background: 'var(--tn-red)', border: 'none', color: '#fff', fontWeight: 600,
            }}>Stop</button>
          )}
          <button className="q-btn" onClick={(e) => { e.stopPropagation(); onNavigate(); }} style={{
            padding: '1px 6px', fontSize: 9, borderRadius: 3, cursor: 'pointer',
            background: 'var(--tn-blue)', border: 'none', color: '#fff', fontWeight: 600,
          }}>{conv.status === 'completed' ? 'Fortsetzen' : 'Oeffnen'}</button>
        </div>
      </div>

      {/* Line 2: Preview + metadata */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
        {previewText && (
          <span style={{
            fontSize: 10, color: 'var(--tn-text-muted)', opacity: 0.6,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {previewText}
          </span>
        )}
        {!previewText && <span style={{ flex: 1 }} />}
        <span style={{ fontSize: 9, color: 'var(--tn-text-muted)', opacity: 0.4, flexShrink: 0, fontFamily: 'monospace' }}>
          {conv.messageCount} msgs
        </span>
        {conv.model && (
          <span style={{ fontSize: 9, color: 'var(--tn-text-muted)', opacity: 0.3, flexShrink: 0 }}>
            {conv.model.replace('claude-', '').replace(/-\d+$/, '')}
          </span>
        )}
        <span style={{ fontSize: 9, color: 'var(--tn-text-muted)', opacity: 0.4, flexShrink: 0 }}>
          {timeAgo(conv.updatedAt)}
        </span>
      </div>
    </div>
  );
}

// --- Main Component ---
export default function QueueOverlay({ accountId, projectId, workDir, useLocal, onNavigate, onStartNew, refreshSignal, simple = false }: QueueOverlayProps) {
  // Load cached conversations for instant display
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    try {
      const cached = localStorage.getItem(`cui-convs-${workDir || 'all'}`);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(() => {
    try { return !localStorage.getItem(`cui-convs-${workDir || 'all'}`); } catch { return true; }
  });
  /*
   * AUCH HIER GEHT NICHTS MEHR VERLOREN (Rafael, 07.09. 18:47Z).
   *
   * Der Beweislauf nach dem Chat-Fix zeigte: das Feld, das im Fenster offen stand,
   * war gar nicht der Chat, sondern DIESES — "Aufgabe beschreiben". Ein Rebuild
   * haette den beschriebenen Auftrag genauso verschluckt. Wer nur den Chat
   * absichert, hat die Haelfte gemacht und weiss es nicht.
   *
   * Betreff und Text getrennt gesichert, weil sie getrennt getippt werden.
   */
  const [subject, setSubject] = useState(() => leseEntwurf(entwurfSchluessel(null, 'neue-aufgabe-betreff')));
  const [message, setMessage] = useState(() => leseEntwurf(entwurfSchluessel(null, 'neue-aufgabe-text')));
  useEffect(() => { schreibeEntwurf(entwurfSchluessel(null, 'neue-aufgabe-betreff'), subject); }, [subject]);
  useEffect(() => { schreibeEntwurf(entwurfSchluessel(null, 'neue-aufgabe-text'), message); }, [message]);
  const { user: authUser, authEnabled } = useAuth();
  // Auth disabled = dev-server = admin (all models). Auth enabled: use the
  // server-provided entitlement so the picker matches what the server accepts.
  const isAdmin = !authEnabled || authUser?.role === 'admin';
  const allowedModels = authUser?.allowedModels ?? (isAdmin ? ['sonnet', 'haiku', 'fable', 'opus'] : ['opus']);
  const [model, setModel] = useState<string>(
    (authUser?.defaultModel as 'sonnet' | 'opus' | 'fable') || (isAdmin ? 'sonnet' : 'opus'),
  );
  const [showCompleted, setShowCompleted] = useState(false);
  const isCodex = ACCOUNTS.find(a => a.id === accountId)?.provider === 'codex';
  // Guenstigerer Default (Rafael 14.09.2026) — Astra bleibt waehlbar, ist dann
  // aber eine bewusste Wahl statt des Anfangswerts.
  const [codexModel, setCodexModel] = useState('gpt-5.6-sol');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const subjectRef = useRef<HTMLInputElement>(null);

  // --- Start Templates ---
  interface PromptTemplate { id: string; label: string; message: string; category: "reply" | "start"; subject?: string; order: number; createdAt: string; }
  const [startTemplates, setStartTemplates] = useState<PromptTemplate[]>([]);
  const [showStartTplForm, setShowStartTplForm] = useState(false);
  const [newStartTplLabel, setNewStartTplLabel] = useState("");
  const [newStartTplSubject, setNewStartTplSubject] = useState("");
  const [newStartTplMessage, setNewStartTplMessage] = useState("");
  const [editingStartTpl, setEditingStartTpl] = useState<PromptTemplate | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>(null);

  useEffect(() => { ensureStyles(); }, []);

  const account = ACCOUNTS.find(a => a.id === accountId) ?? ACCOUNTS[0];

  // Fetch conversations
  const lastCountRef = useRef(-1);
  const fetchConversations = useCallback(() => {
    if (window.__cuiServerAlive === false) { setLoading(false); return; }
    // status=all: die "Abgeschlossen"-Sektion unten braucht status==='completed'.
    // Der Server-Default liefert seit 2026-08-17 NUR offene Chats (Rule 1), womit
    // `completed` strukturell leer blieb — die Sektion war da, ihre Daten nicht.
    // ohneHof=1: Hof-Berater im Namen eines Partners stehen nicht in seiner Chatliste
    // (Rafael 25.10.2026; wirkt nur fuer Nicht-Admins, server/routes/shared/conversation-scope.ts).
    // trust-boundary:ok steht auf der Zeile, weil die L0-Pruefung Befunde am Zeilentext festmacht: der Abruf ist
    // Altbestand (raw_fetch in der Basis), geaendert ist nur der Parameter ohneHof; die Antwort bleibt wie bisher.
    fetch(conversationsUrl(`${API}/mission/conversations`, { status: 'all', ohneHof: '1' }), { signal: AbortSignal.timeout(10000) }) // trust-boundary:ok
      .then(r => { if (!r.ok) throw new Error('fetch failed'); return r.json(); })
      .then(data => {
        const convs: Conversation[] = data.conversations || [];
        const REMOTE_IDS = new Set(['engelmann', 'office', 'gmail', 'werking']);
        const REMOTE_WS_PREFIX = '/root/orchestrator/workspaces/';
        const LOCAL_WS_PREFIX_MATCH = '/.cui/workspaces/';
        const wsName = workDir?.startsWith(REMOTE_WS_PREFIX)
          ? workDir.slice(REMOTE_WS_PREFIX.length).replace(/\/$/, '')
          : '';
        const filtered = convs.filter(c => {
          // Show conversations from ALL accounts (no account filtering)
          // Only filter local vs remote: in local mode hide remote, in remote mode hide local
          if (useLocal && c.accountId !== 'local') return false;
          if (!useLocal && c.accountId === 'local') return false;
          // Partner (non-admin): server already scoped to this user. For multi-workspace
          // partners (David, Sahori, Kurt), sessions must be tagged with projectId —
          // without it all workspaces would show the same list. Untagged sessions fall
          // back to workDir path matching (legacy behaviour).
          if (projectId && (c as any).projectId) {
            return (c as any).projectId === projectId;
          }
          if (!isAdmin) return true;
          if (workDir) {
            const pp = (c.projectPath || '').replace(/\/$/, '');
            const wd = workDir.replace(/\/$/, '');
            if (pp === wd || pp.startsWith(wd + '/')) return true;
            if (wsName && (pp.includes(LOCAL_WS_PREFIX_MATCH + wsName) || pp.endsWith('/' + wsName))) return true;
            return false;
          }
          return true;
        });
        // Only log when count changes (avoid console spam)
        if (filtered.length !== lastCountRef.current) {
          console.log(`[QueueOverlay:${accountId}] ${filtered.length} conversations (${convs.length} total)`);
          lastCountRef.current = filtered.length;
        }
        setConversations(filtered);
        setLoading(false);
        // Cache for instant load next time
        try { localStorage.setItem(`cui-convs-${workDir || 'all'}`, JSON.stringify(filtered.slice(0, 20))); } catch {} // silent-ok: localStorage may be disabled
      })
      .catch((err) => { console.warn('[QueueOverlay] fetchConversations:', err); setLoading(false); });
  }, [workDir, useLocal, isAdmin, projectId]); // accountId intentionally excluded — conversations are shown for ALL accounts

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(fetchConversations, 60000);
    // Sicht-Schalter (Nur meine | Alle) umgestellt → sofort neu laden, nicht erst in 60 s.
    window.addEventListener(SCOPE_CHANGED_EVENT, fetchConversations);
    return () => { if (pollRef.current) clearInterval(pollRef.current); window.removeEventListener(SCOPE_CHANGED_EVENT, fetchConversations); };
  }, [fetchConversations]);

  // Refresh when parent signals via WS — debounced to prevent request floods
  useEffect(() => {
    if (refreshSignal && refreshSignal > 0) {
      const timeout = setTimeout(fetchConversations, 2000);
      return () => clearTimeout(timeout);
    }
  }, [refreshSignal, fetchConversations]);

  // Split conversations — Sub-Sessions are never shown here (Rule 2, see utils/subSession.ts).
  const active = useMemo(
    () => conversations.filter(c => c.status === 'ongoing' && !isSubSession(c)).sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    ),
    [conversations]
  );
  const completed = useMemo(
    () => conversations.filter(c => c.status === 'completed' && !isSubSession(c)).sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    ),
    [conversations]
  );
  // Nur Partner-Sicht: Rafaels Liste bleibt unveraendert.
  const titelZusaetze = useMemo(
    () => (isAdmin ? new Map<string, string>() : datumsZusaetze([...active, ...completed])),
    [isAdmin, active, completed]
  );

  // Start new conversation

  // --- Fetch Start Templates ---
  useEffect(() => {
    if (window.__cuiServerAlive !== true) return;
    fetch('/api/prompt-templates', { signal: AbortSignal.timeout(10000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const start = (data.templates || []).filter((t: PromptTemplate) => t.category === 'start');
        start.sort((a: PromptTemplate, b: PromptTemplate) => a.order - b.order);
        setStartTemplates(start);
      })
      .catch(() => { /* templates load lazily */ });
  }, []);

  const handleSaveStartTemplate = useCallback(async () => {
    if (window.__cuiServerAlive === false) return;
    if (!newStartTplLabel.trim() || !newStartTplMessage.trim()) return;
    try {
      if (editingStartTpl) {
        const resp = await fetch(`/api/prompt-templates/${editingStartTpl.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: newStartTplLabel, message: newStartTplMessage, subject: newStartTplSubject, category: 'start' }),
          signal: AbortSignal.timeout(15000),
        });
        if (!resp.ok) throw new Error(`PUT prompt-templates ${resp.status}`);
        const data = await resp.json();
        setStartTemplates(prev => prev.map(t => t.id === data.template.id ? data.template : t));
      } else {
        const resp = await fetch('/api/prompt-templates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: newStartTplLabel, message: newStartTplMessage, subject: newStartTplSubject, category: 'start' }),
          signal: AbortSignal.timeout(15000),
        });
        if (!resp.ok) throw new Error(`POST prompt-templates ${resp.status}`);
        const data = await resp.json();
        setStartTemplates(prev => [...prev, data.template]);
      }
    } catch (err) {
      console.warn('[QueueOverlay] handleSaveStartTemplate:', err);
    }
    setShowStartTplForm(false);
    setEditingStartTpl(null);
    setNewStartTplLabel('');
    setNewStartTplSubject('');
    setNewStartTplMessage('');
  }, [newStartTplLabel, newStartTplSubject, newStartTplMessage, editingStartTpl]);

  const handleDeleteStartTemplate = useCallback(async (id: string) => {
    if (window.__cuiServerAlive === false) return;
    try {
      const resp = await fetch(`/api/prompt-templates/${id}`, { method: 'DELETE', signal: AbortSignal.timeout(15000) });
      if (!resp.ok) throw new Error(`DELETE prompt-templates ${resp.status}`);
      setStartTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.warn('[QueueOverlay] handleDeleteStartTemplate:', err);
    }
  }, []);

  const handleStart = useCallback(async () => {
    if (!subject.trim() || !message.trim() || starting) return;
    setStarting(true);
    setStartError('');
    const ok = await onStartNew(subject.trim(), message.trim(), isCodex ? codexModel : model);
    if (ok) {
      setSubject('');
      setMessage('');
    } else {
      setStartError('Konversation konnte nicht gestartet werden');
    }
    setStarting(false);
  }, [subject, message, model, isCodex, codexModel, starting, onStartNew]);

  // Stop conversation
  const handleStop = useCallback((conv: Conversation) => {
    if (window.__cuiServerAlive === false) return;
    fetch(`${API}/mission/conversation/${conv.accountId}/${conv.sessionId}/stop`, { method: 'POST', signal: AbortSignal.timeout(15000) })
      .then(r => { if (!r.ok) throw new Error(`stop ${r.status}`); })
      .then(() => setTimeout(fetchConversations, 1000))
      .catch((err) => console.warn('[QueueOverlay] handleStop:', err));
  }, [fetchConversations]);

  // Set custom name
  const handleSetName = useCallback((conv: Conversation, name: string) => {
    if (window.__cuiServerAlive === false) return;
    fetch(`${API}/mission/conversation/${conv.accountId}/${conv.sessionId}/name`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ custom_name: name }),
      signal: AbortSignal.timeout(15000),
    }).then(r => { if (!r.ok) throw new Error(`setName ${r.status}`); })
      .then(() => setTimeout(fetchConversations, 500))
      .catch((err) => console.warn('[QueueOverlay] handleSetName:', err));
  }, [fetchConversations]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'var(--tn-surface)', color: 'var(--tn-text)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: 'var(--tn-bg-dark)',
        borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: account.color }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)' }}>
            {simple ? 'Meine Chats' : account.label}
          </span>
          {projectId && !simple && (
            <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }}>
              {projectId}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!simple && (
            <>
              <span style={{ fontSize: 10, color: 'var(--tn-green)', fontWeight: 600 }}>
                {active.filter(c => c.streamingId).length} streaming
              </span>
              <span style={{ fontSize: 10, color: 'var(--tn-orange)' }}>
                {active.length} aktiv
              </span>
              <span style={{ fontSize: 10, color: 'var(--tn-text-muted)', opacity: 0.5 }}>
                {completed.length} fertig
              </span>
            </>
          )}
        </div>

        {/* New conversation form */}
        <div style={{
          background: 'var(--tn-bg)', border: '1px solid var(--tn-border)',
          borderRadius: 6, padding: 10,
        }}>
          {/* Start Template Chips */}
          {startTemplates.length > 0 && !showStartTplForm && !simple && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
              {startTemplates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={(e) => { if (e.altKey) { setEditingStartTpl(tpl); setNewStartTplLabel(tpl.label); setNewStartTplSubject(tpl.subject || ""); setNewStartTplMessage(tpl.message); setShowStartTplForm(true); return; } setSubject(tpl.subject || ""); setMessage(tpl.message); }}
                  onContextMenu={(e) => { e.preventDefault(); if (confirm(`"${tpl.label}" löschen?`)) handleDeleteStartTemplate(tpl.id); }}
                  title={`${tpl.subject ? tpl.subject + ": " : ""}${tpl.message}\n\nAlt+Klick = Bearbeiten | Rechtsklick = Löschen`}
                  style={{ padding: "3px 8px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", color: "var(--tn-blue)", whiteSpace: "nowrap", fontFamily: "inherit" }}
                >
                  {tpl.label}
                </button>
              ))}
              <button
                onClick={() => { setShowStartTplForm(true); setEditingStartTpl(null); setNewStartTplLabel(""); setNewStartTplSubject(""); setNewStartTplMessage(""); }}
                title="Neues Start-Template"
                style={{ padding: "3px 6px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: "transparent", border: "1px dashed var(--tn-border)", color: "var(--tn-text-muted)", opacity: 0.5 }}
              >
                +
              </button>
            </div>
          )}
          {showStartTplForm && (
            <div style={{ display: "flex", gap: 4, marginBottom: 6, flexWrap: "wrap" }}>
              <input value={newStartTplLabel} onChange={e => setNewStartTplLabel(e.target.value)} placeholder="Label" style={{ width: 70, padding: "3px 6px", fontSize: 10, background: "var(--tn-bg)", color: "var(--tn-text)", border: "1px solid var(--tn-blue)", borderRadius: 3, fontFamily: "inherit" }} />
              <input value={newStartTplSubject} onChange={e => setNewStartTplSubject(e.target.value)} placeholder="Betreff" style={{ width: 80, padding: "3px 6px", fontSize: 10, background: "var(--tn-bg)", color: "var(--tn-text)", border: "1px solid var(--tn-blue)", borderRadius: 3, fontFamily: "inherit" }} />
              <input value={newStartTplMessage} onChange={e => setNewStartTplMessage(e.target.value)} placeholder="Nachricht" onKeyDown={e => { if (e.key === "Enter") handleSaveStartTemplate(); if (e.key === "Escape") { setShowStartTplForm(false); setEditingStartTpl(null); } }} style={{ flex: 1, minWidth: 120, padding: "3px 6px", fontSize: 10, background: "var(--tn-bg)", color: "var(--tn-text)", border: "1px solid var(--tn-blue)", borderRadius: 3, fontFamily: "inherit" }} />
              <button onClick={handleSaveStartTemplate} disabled={!newStartTplLabel.trim() || !newStartTplMessage.trim()} style={{ padding: "3px 8px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: newStartTplLabel.trim() && newStartTplMessage.trim() ? "var(--tn-blue)" : "var(--tn-border)", border: "none", color: "#fff", fontWeight: 600 }}>{editingStartTpl ? "Update" : "Save"}</button>
              <button onClick={() => { setShowStartTplForm(false); setEditingStartTpl(null); }} style={{ padding: "3px 6px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: "transparent", border: "1px solid var(--tn-border)", color: "var(--tn-text-muted)" }}>X</button>
            </div>
          )}
          <input
            ref={subjectRef}
            value={subject}
            onChange={e => { setSubject(e.target.value); setStartError(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey && subject.trim()) {
                // Focus message field
                const ta = e.currentTarget.parentElement?.querySelector('textarea');
                if (ta) ta.focus();
              }
            }}
            placeholder={simple ? 'Worum geht es? (z. B. „Angebot Firma Muster")' : 'Betreff (Pflicht)'}
            style={{
              width: '100%', padding: '5px 8px', fontSize: 12,
              background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
              border: '1px solid var(--tn-border)', borderRadius: 4,
              marginBottom: 6, boxSizing: 'border-box',
              fontWeight: 600,
            }}
          />
          <div style={{ display: simple ? 'none' : 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--tn-text-muted)', whiteSpace: 'nowrap' }}>Modell:</span>
            <select value={isCodex ? codexModel : model} onChange={e => isCodex ? setCodexModel(e.target.value) : setModel(e.target.value)}
              style={{
                padding: '2px 6px', fontSize: 10, borderRadius: 3,
                background: model === 'opus' ? 'var(--tnfh-2d2040)' : model === 'fable' ? 'var(--tnfh-1a3020)' : 'var(--tn-bg-dark)',
                color: model === 'opus' ? 'var(--tn-purple)' : model === 'fable' ? 'var(--tnf-7ec699)' : 'var(--tn-text)',
                border: `1px solid ${model === 'opus' ? 'var(--tn-purple)' : model === 'fable' ? 'var(--tnf-7ec699)' : 'var(--tn-border)'}`,
                cursor: 'pointer', fontWeight: (model === 'opus' || model === 'fable') ? 600 : 400,
              }}>
              {!isCodex && <option value="sonnet">Sonnet</option>}
              {isCodex && <option value="gpt-5.6-sol">Sol (Standard)</option>}
              {isCodex && <option value="gpt-5.6-terra">Terra</option>}
              {isCodex && <option value="gpt-6-astra">Astra (nur fuer richtig komplexe Aufgaben)</option>}
              {!isCodex && allowedModels.includes('fable') && <option value="fable">Fable (Promo)</option>}
              {!isCodex && allowedModels.includes('opus') && <option value="opus">Opus</option>}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <textarea
              value={message}
              onChange={e => { setMessage(e.target.value); setStartError(''); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleStart();
                }
              }}
              placeholder={simple ? 'Deine Frage oder Aufgabe… (Strg+Enter startet)' : 'Aufgabe beschreiben... (Cmd+Enter zum Starten)'}
              rows={2}
              style={{
                flex: 1, padding: '5px 8px', fontSize: 11,
                background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
                border: '1px solid var(--tn-border)', borderRadius: 4,
                resize: 'vertical', minHeight: 32, maxHeight: 100,
                fontFamily: 'inherit', lineHeight: 1.4, boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleStart}
              disabled={!subject.trim() || !message.trim() || starting}
              style={{
                padding: '5px 14px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                background: subject.trim() && message.trim() ? 'var(--tn-blue)' : 'var(--tn-border)',
                border: 'none', color: '#fff', fontWeight: 600, alignSelf: 'flex-end',
                opacity: subject.trim() && message.trim() ? 1 : 0.4,
                whiteSpace: 'nowrap',
              }}
            >
              {starting ? '...' : 'Start'}
            </button>
          </div>
          {startError && (
            <div style={{
              marginTop: 6, padding: '4px 8px', fontSize: 11, fontWeight: 600,
              color: 'var(--tn-red)', background: 'rgba(247,118,142,0.1)',
              borderRadius: 4, border: '1px solid rgba(247,118,142,0.3)',
            }}>
              {startError}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--tn-text-muted)', fontSize: 11 }}>
            Lade...
          </div>
        )}

        {/* Active conversations */}
        {active.length > 0 && (
          <>
            <div style={{
              padding: '6px 12px', fontSize: 10, fontWeight: 700,
              color: 'var(--tn-orange)', textTransform: 'uppercase', letterSpacing: 0.5,
              background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)',
              position: 'sticky', top: 0, zIndex: 1,
            }}>
              Aktiv ({active.length})
            </div>
            {active.map(conv => (
              <ConvRow
                key={conv.sessionId}
                conv={conv}
                onNavigate={() => onNavigate(conv.sessionId)}
                onStop={() => handleStop(conv)}
                onSetName={(name) => handleSetName(conv, name)}
                titelZusatz={titelZusaetze.get(conv.sessionId)}
              />
            ))}
          </>
        )}

        {/* Completed conversations */}
        {completed.length > 0 && (
          <>
            <div
              onClick={() => setShowCompleted(!showCompleted)}
              style={{
                padding: '6px 12px', fontSize: 10, fontWeight: 700,
                color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5,
                background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                position: 'sticky', top: active.length > 0 ? 0 : undefined, zIndex: 1,
              }}
            >
              <span style={{ fontSize: 9, transition: 'transform 0.15s', transform: showCompleted ? 'rotate(90deg)' : 'none' }}>
                ▶
              </span>
              Abgeschlossen ({completed.length})
            </div>
            {showCompleted && completed.slice(0, 30).map(conv => (
              <ConvRow
                key={conv.sessionId}
                conv={conv}
                onNavigate={() => onNavigate(conv.sessionId)}
                onStop={() => {}}
                onSetName={(name) => handleSetName(conv, name)}
                titelZusatz={titelZusaetze.get(conv.sessionId)}
              />
            ))}
            {showCompleted && completed.length > 30 && (
              <div style={{ padding: '8px 12px', fontSize: 10, color: 'var(--tn-text-muted)', textAlign: 'center' }}>
                +{completed.length - 30} weitere
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!loading && conversations.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--tn-text-muted)', marginBottom: 8 }}>
              {simple ? 'Noch keine Gespräche' : 'Keine Konversationen in diesem Workspace'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', opacity: 0.5 }}>
              {simple ? 'Oben eins beginnen — ein Stichwort, worum es geht, und deine Frage.' : 'Starte oben eine neue Konversation mit Betreff'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
